import React, { type ReactNode } from 'react';
import {
  STEPPER_DENSITY_REGULAR,
  STEPPER_STEP_STATE_ACTIVE,
  STEPPER_STEP_STATE_COMPLETED,
  STEPPER_STEP_STATE_UPCOMING,
  type StepperDensity,
  type StepperStepState,
} from './Stepper.constants';
import {
  Connector,
  Description,
  Indicator,
  Item,
  Label,
  Root,
  StepButton,
  TextCol,
} from './Stepper.styled';

export interface StepperStep {
  /** Stable id used as the React key. */
  id: string;
  /** Short label shown next to the indicator. */
  label: string;
  /** Optional secondary line under the label. */
  description?: ReactNode;
}

export interface StepperProps {
  steps: StepperStep[];
  /** Zero-based index of the active step. */
  current: number;
  /**
   * Navigate to a step. Only completed steps (index < current) are
   * navigable; upcoming steps are inert. Omit to make the stepper a pure
   * indicator.
   */
  onStepClick?: (index: number) => void;
  density?: StepperDensity;
  ariaLabel?: string;
  className?: string;
  dataTestId?: string;
}

const Check = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M5 12.5 10 17 19 7"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const stateFor = (index: number, current: number): StepperStepState => {
  if (index < current) return STEPPER_STEP_STATE_COMPLETED;
  if (index === current) return STEPPER_STEP_STATE_ACTIVE;
  return STEPPER_STEP_STATE_UPCOMING;
};

/**
 * Horizontal wizard step indicator: numbered indicators with labels and
 * connector lines, one per step. Completed steps show a check and can be
 * clicked to navigate back (when `onStepClick` is supplied); the active
 * step is emphasised; upcoming steps are muted and inert.
 *
 * @example
 *   <Stepper
 *     current={step}
 *     onStepClick={setStep}
 *     steps={[
 *       { id: 'details', label: 'Details' },
 *       { id: 'config', label: 'Config' },
 *       { id: 'auth', label: 'Auth' },
 *     ]}
 *   />
 */
const Stepper: React.FC<StepperProps> = ({
  steps,
  current,
  onStepClick,
  density = STEPPER_DENSITY_REGULAR,
  ariaLabel = 'Progress',
  className,
  dataTestId = 'stepper',
}) => {
  return (
    <Root
      aria-label={ariaLabel}
      className={className}
      data-testid={dataTestId}
      data-density={density}
    >
      {steps.map((step, index) => {
        const state = stateFor(index, current);
        const isLast = index === steps.length - 1;
        const clickable =
          !!onStepClick && state === STEPPER_STEP_STATE_COMPLETED;

        return (
          <Item key={step.id} $isLast={isLast} data-testid={`${dataTestId}__item`}>
            <StepButton
              type="button"
              $density={density}
              $clickable={clickable}
              disabled={!clickable}
              aria-current={state === STEPPER_STEP_STATE_ACTIVE ? 'step' : undefined}
              onClick={clickable ? () => onStepClick(index) : undefined}
              data-testid={`${dataTestId}__step-${step.id}`}
              data-state={state}
            >
              <Indicator
                $state={state}
                $density={density}
                data-testid={`${dataTestId}__indicator`}
              >
                {state === STEPPER_STEP_STATE_COMPLETED ? <Check /> : index + 1}
              </Indicator>
              <TextCol>
                <Label $state={state} $density={density}>
                  {step.label}
                </Label>
                {step.description !== undefined && step.description !== null && (
                  <Description $density={density}>{step.description}</Description>
                )}
              </TextCol>
            </StepButton>
            {!isLast && (
              <Connector
                $done={state === STEPPER_STEP_STATE_COMPLETED}
                data-testid={`${dataTestId}__connector`}
              />
            )}
          </Item>
        );
      })}
    </Root>
  );
};

export default Stepper;
